import { useCallback, useEffect, useRef, useState } from "react";
import { playTtsQueue, unlockAudio, type TtsAbortHandle } from "../lib/ttsQueue";

export type VoiceState = "idle" | "listening" | "thinking" | "speaking";

interface UseVoiceOptions {
  lang?: string;
  onTranscript?: (text: string) => void;
}

interface UseVoiceReturn {
  state: VoiceState;
  supported: boolean;
  startListening: () => void;
  stopListening: () => void;
  speak: (text: string) => Promise<void>;
  stopSpeaking: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpeechRecognition = any;

export function useVoice({ lang = "ru-RU", onTranscript }: UseVoiceOptions = {}): UseVoiceReturn {
  const [state, setState] = useState<VoiceState>("idle");
  const recognitionRef = useRef<AnySpeechRecognition>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const abortRef = useRef<TtsAbortHandle | null>(null);

  const SpeechRecognitionCtor =
    typeof window !== "undefined"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ? ((window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition)
      : null;

  const supported = Boolean(SpeechRecognitionCtor);

  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      if (abortRef.current) abortRef.current.cancelled = true;
      try { audioRef.current?.pause(); } catch { /* no-op */ }
    };
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionCtor) return;
    recognitionRef.current?.abort();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rec = new (SpeechRecognitionCtor as any)();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = false;

    rec.onstart = () => setState("listening");
    rec.onresult = (e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => {
      const transcript = Array.from(e.results)
        .map((r) => (r[0] as { transcript: string }).transcript)
        .join(" ")
        .trim();
      if (transcript) {
        setState("thinking");
        onTranscript?.(transcript);
      }
    };
    rec.onerror = () => setState("idle");
    rec.onend = () => setState((s) => s === "listening" ? "idle" : s);

    recognitionRef.current = rec;
    rec.start();
  }, [SpeechRecognitionCtor, lang, onTranscript]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setState("idle");
  }, []);

  const cancelCurrentPlayback = useCallback(() => {
    if (abortRef.current) abortRef.current.cancelled = true;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* no-op */ }
      try { audioRef.current.removeAttribute("src"); audioRef.current.load(); } catch { /* no-op */ }
    }
  }, []);

  const speak = useCallback(async (text: string) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;

    unlockAudio();
    cancelCurrentPlayback();
    setState("speaking");

    const abort: TtsAbortHandle = { cancelled: false };
    abortRef.current = abort;

    try {
      await playTtsQueue(trimmed, audioRef, abort);
    } catch (err) {
      // NO browser fallback — just log. No more robot woman voice.
      // eslint-disable-next-line no-console
      console.error("[voice] TTS failed (no fallback):", err);
    } finally {
      if (abortRef.current === abort) abortRef.current = null;
      setState("idle");
    }
  }, [cancelCurrentPlayback]);

  const stopSpeaking = useCallback(() => {
    cancelCurrentPlayback();
    setState("idle");
  }, [cancelCurrentPlayback]);

  return { state, supported, startListening, stopListening, speak, stopSpeaking };
}
